import React, { useState } from "react";
import { Loader2, Calendar, DollarSign, Sparkles, Building } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function BrandProposalForm({ onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    submission_type: "brand",
    brand_name: "",
    brand_stage: "new_inquiry",
    brand_website: "",
    contact_person: "",
    contact_email: "",
    twitter_handle: "",
    instagram_handle: "",
    platform: "",
    content_type: "",
    compensation_type: "unknown",
    deal_value: 0,
    deliverables_count: 0,
    due_date: "",
    product_offer_description: ""
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "number" ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log("Brand form submit triggered", formData);
    
    if (!formData.brand_name || !formData.contact_email) {
      toast.error("Please fill in brand name and email");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        name: formData.brand_name,
        email: formData.contact_email
      };
      console.log("Sending brand submission:", payload);
      
      const response = await api.post("/api/submissions/", payload);
      console.log("Brand submission response:", response);
      
      toast.success("Brand proposal submitted!");
      if (typeof onSuccess === "function") {
        onSuccess();
      }
    } catch (error) {
      console.error("Brand submission error:", error);
      const errorMsg = error?.response?.data?.detail || error?.message || "Failed to submit";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Brand Proposal</h2>
        <p className="text-gray-400">Partner for sponsored content</p>
      </div>

      {/* Brand Info */}
      <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 border border-white/10 space-y-4">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <Building className="w-5 h-5 text-amber-400" />
          Brand Information
        </h3>
        
        <div>
          <label className="block text-gray-300 mb-2 font-medium">Brand Name <span className="text-pink-400">*</span></label>
          <input type="text" name="brand_name" value={formData.brand_name} onChange={handleChange} required
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition"
            placeholder="Your brand name" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Brand Website</label>
            <input type="url" name="brand_website" value={formData.brand_website} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition"
              placeholder="https://yourbrand.com" />
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Platform</label>
            <input type="text" name="platform" value={formData.platform} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition"
              placeholder="OnlyFans, X, Instagram, etc." />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">X (Twitter) Handle</label>
            <input type="text" name="twitter_handle" value={formData.twitter_handle} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition"
              placeholder="@yourbrand" />
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Instagram Handle</label>
            <input type="text" name="instagram_handle" value={formData.instagram_handle} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition"
              placeholder="@yourbrand" />
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 border border-white/10 space-y-4">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          Contact Person
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Name</label>
            <input type="text" name="contact_person" value={formData.contact_person} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition"
              placeholder="Your name" />
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Email <span className="text-pink-400">*</span></label>
            <input type="email" name="contact_email" value={formData.contact_email} onChange={handleChange} required
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition"
              placeholder="contact@brand.com" />
          </div>
        </div>
      </div>

      {/* Partnership Details */}
      <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-6 border border-white/10 space-y-4">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-amber-400" />
          Partnership Details
        </h3>

        <div>
          <label className="block text-gray-300 mb-2 font-medium">Content Type</label>
          <input type="text" name="content_type" value={formData.content_type} onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition"
            placeholder="Instagram posts, YouTube video, OnlyFans promo, etc." />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Compensation Type</label>
            <select name="compensation_type" value={formData.compensation_type} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white focus:border-amber-500 focus:outline-none transition">
              <option value="unknown">Unknown / TBD</option>
              <option value="paid">Paid</option>
              <option value="product">Product / Gift</option>
              <option value="affiliate">Affiliate Commission</option>
              <option value="hybrid">Hybrid (Paid + Product/Affiliate)</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Deal Value (€)</label>
            <input type="number" name="deal_value" value={formData.deal_value} onChange={handleChange} min="0" step="0.01"
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition"
              placeholder="e.g., 500" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Number of Deliverables</label>
            <input type="number" name="deliverables_count" value={formData.deliverables_count} onChange={handleChange} min="0"
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition"
              placeholder="e.g., 3 posts + 1 story = 4" />
          </div>
          <div>
            <label className="block text-gray-300 mb-2 font-medium">Due Date</label>
            <input type="date" name="due_date" value={formData.due_date} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white focus:border-amber-500 focus:outline-none transition" />
          </div>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-medium">Product / Offer Description</label>
          <textarea name="product_offer_description" value={formData.product_offer_description} onChange={handleChange} rows={4}
            className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/10 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none transition resize-none"
            placeholder="Describe your proposal, what you're offering, what you need from Gio..." />
        </div>
      </div>

      <button type="submit" disabled={loading}
        className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 text-white font-bold text-lg hover:from-amber-600 hover:to-yellow-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-amber-500/50">
        {loading ? (<><Loader2 className="w-5 h-5 animate-spin" />Submitting...</>) : "Submit Brand Proposal"}
      </button>
    </form>
  );
}